import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Field";
import { Notice } from "../ui/Notice";
import { Modal } from "../ui/Overlay";
import { Segmented } from "../ui/Panel";
import { Tag } from "../ui/Tag";
import { PlayerSelect } from "./PlayerSelect";
import { readableId } from "./PlayerIdent";
import {
  deletePlayerCostSource, fetchPlayerCostSources, logAction, putPlayerCostSources,
  type PlayerCostSourcePayload, type PlayerCostSourceRow,
} from "../../lib/api";
import { costSourceKind, costSourceName, costSourceRival, COST_SOURCE_FORMULA, COST_SOURCE_MANUAL, rivalSource } from "../../lib/costSource";
import type { RivalDef } from "../../lib/fixtures";
import { useT } from "../../lib/i18n";
import { formatAt } from "../../lib/time";
import { useTz } from "../../lib/tz-context";

/**
 * 按玩家指定造价来源（设计稿 ui.jsx PlayerSourceDialog；leo 2026-09-17「一个版本同时测竞品和公式」）。
 *
 * 版本默认来源对所有人生效；这里指定的玩家单独用另一套价。指定按代理存、**不跟版本走**、不进版本快照，
 * 所以草稿与已发布版本都能点，改完 30 秒内生效（游戏端按代理整表缓存）。客户端零改动。
 *
 * 可选项用**当前页面这一版**的竞品列（rivals）：运营正在看的就是它。指定的列不在生效版本里时服务端标 missingInActive，
 * 这里显示红字「生效版本没有这列，按版本默认」，不静默。
 */
export function PlayerSourceDialog({
  rivals, defaultSource, unit, onClose, onChanged,
}: {
  rivals: RivalDef[];
  /** 当前页面这一版的默认来源（来源条上选的那个） */
  defaultSource: string;
  unit: "level" | "pettownStar";
  onClose: () => void;
  /** 增删之后回调人数，调用方更新按钮上的数字 */
  onChanged?: (count: number) => void;
}) {
  const t = useT();
  const tz = useTz();
  const [data, setData] = useState<PlayerCostSourcePayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [source, setSource] = useState(COST_SOURCE_FORMULA);
  const [note, setNote] = useState("");
  const load = () => fetchPlayerCostSources().then((d) => { setData(d); setErr(null); onChanged?.(d.rows.length); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const names = { formula: t("cost.src.formula"), manual: t("cost.src.manual") };
  // 名字优先按本页这一版的列名；不在本页的列（老版本留下的）退回生效版本的列名，再退回 id
  const allRivals: RivalDef[] = [
    ...rivals,
    ...(data?.activeRivals ?? []).filter((r) => !rivals.some((x) => x.id === r.rivalId)).map((r) => ({ id: r.rivalId, name: r.name, color: "" })),
  ];
  const options = [
    { value: COST_SOURCE_FORMULA, label: names.formula },
    { value: COST_SOURCE_MANUAL, label: names.manual },
    ...rivals.map((r) => ({ value: rivalSource(r.id), label: r.name })),
  ];
  const tone = (v: string): "primary" | "success" | "warning" => costSourceKind(v) === "formula" ? "primary" : costSourceKind(v) === "manual" ? "success" : "warning";
  const run = async (fn: () => Promise<PlayerCostSourcePayload>) => {
    setBusy(true);
    try { const d = await fn(); setData(d); setErr(null); onChanged?.(d.rows.length); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const add = () => run(async () => {
    const d = await putPlayerCostSources(picked, source, note.trim());
    logAction("player.costSource.set", picked.join(","), { source, note: note.trim() });
    setPicked([]); setNote("");
    return d;
  });
  const remove = (r: PlayerCostSourceRow) => run(async () => {
    const d = await deletePlayerCostSource(r.userIdHex);
    logAction("player.costSource.clear", r.userIdHex);
    return d;
  });
  const unitText = t(unit === "level" ? "u.level" : "u.pettownStar");
  // 与页眉同一个时区（代理时区），别再给运营一个 UTC
  const fmt = (ms: number) => formatAt(ms, tz);
  const thCls = "h-8 whitespace-nowrap border-b border-line-lighter bg-fill-light px-2 text-left text-xs font-semibold text-ink-regular";
  const tdCls = "h-9 whitespace-nowrap border-b border-line-lighter px-2 text-sm";

  return (
    <Modal title={t("ps.title")} width={760} onClose={onClose} footer={<Button onClick={onClose}>{t("log.close")}</Button>}>
      <div className="flex flex-col gap-3.5">
        <Notice tone="info">{t("ps.intro", { d: costSourceName(defaultSource, allRivals, names) ?? names.formula })}</Notice>
        {err && <Notice tone="danger">{err}</Notice>}
        <div className="flex flex-wrap items-center gap-2.5 rounded-base border border-dashed border-line px-3 py-2.5">
          <span className="text-sm font-semibold">{t("common.add")}</span>
          <PlayerSelect value={picked} labels={labels} onChange={(v, l) => { setPicked(v); setLabels((x) => ({ ...x, ...l })); }} width={240} disabled={busy} />
          <Segmented value={source} options={options} onChange={setSource} />
          <Input value={note} onChange={setNote} placeholder={t("ps.notePh")} className="w-40" disabled={busy} />
          <Button tone="primary" size="sm" disabled={busy || !picked.length} onClick={() => void add()}>{t("ps.add")}</Button>
        </div>
        <table className="w-full border-separate border-spacing-0">
          <thead><tr>
            <th className={thCls}>{t("ps.col.player")}</th><th className={thCls}>{t("ps.col.source")}</th>
            <th className={thCls}>{t("ps.col.note")}</th><th className={thCls}>{t("ps.col.updated")}</th><th className={`${thCls} w-10`} />
          </tr></thead>
          <tbody>
            {!data ? <tr><td colSpan={5} className={`${tdCls} text-center text-ink-placeholder`}>{t("report.loading")}</td></tr>
              : data.rows.length === 0 ? <tr><td colSpan={5} className={`${tdCls} text-center text-ink-placeholder`}>{t("ps.empty")}</td></tr>
              : data.rows.map((r) => (
                <tr key={r.userIdHex}>
                  <td className={tdCls}>
                    <span className="font-medium">{r.nickname || t("ps.noName")}</span>
                    <span className="ml-2 text-xs text-ink-placeholder" title={r.userIdHex}>{readableId(r.userIdHex)}</span>
                  </td>
                  <td className={tdCls}>
                    <Tag tone={r.missingInActive ? "danger" : tone(r.costSource)}>
                      {costSourceName(r.costSource, allRivals, names) ?? costSourceRival(r.costSource) ?? r.costSource}
                    </Tag>
                    {r.missingInActive && <span className="ml-1.5 text-xs text-danger">{t("ps.missing")}</span>}
                    {!r.missingInActive && r.stepInStar > 0 && <span className="ml-1.5 text-xs text-warning">{t("ps.midStar", { u: unitText, n: r.stepInStar })}</span>}
                  </td>
                  <td className={`${tdCls} text-ink-secondary`}>{r.note || "—"}</td>
                  <td className={`${tdCls} text-ink-secondary tabular-nums`}>{fmt(r.updatedAtMs)}</td>
                  <td className={tdCls}>
                    <button type="button" className="rounded-sm px-1 text-sm leading-none text-ink-placeholder hover:bg-danger-50 hover:text-danger"
                      title={t("ps.clear")} aria-label={t("ps.clear")} disabled={busy} onClick={() => void remove(r)}>×</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <div className="text-xs leading-relaxed text-ink-placeholder">{t("ps.foot", { max: data?.max ?? 500, u: unitText })}</div>
      </div>
    </Modal>
  );
}
